import type {
  ProgramCardPayload,
  ProgramCardProgramSummary,
  ProgramCardRatingsVetting,
  ProgramCardEligibility,
  ProgramCardFeesPrizes,
  ProgramCardTimeline,
  ProgramCardUrls,
  ProgramCardContacts,
  ProgramCardProgramDetails,
  ProgramCardCategory,
} from '../types/program-card';
import { isRecord, optionalBoolean, optionalNumber, optionalString, parseJsonArray, parseJsonOptional } from './typeGuards';

const extractProgram = (raw: unknown): ProgramCardProgramSummary | null => {
  const section = parseJsonOptional<Record<string, unknown>>(raw) ?? (isRecord(raw) ? raw : null);
  if (!section) return null;
  return {
    name: optionalString(section.name),
    baci_program_code: optionalString(section.baci_program_code),
    awarding_organization: optionalString(section.awarding_organization),
    foundation_year: optionalNumber(section.foundation_year) ?? optionalString(section.foundation_year) ?? null,
  };
};

const extractRatingsVetting = (raw: unknown): ProgramCardRatingsVetting | null => {
  const section = parseJsonOptional<Record<string, unknown>>(raw) ?? (isRecord(raw) ? raw : null);
  if (!section) return null;
  const vettingNotes = (() => {
    const notes = parseJsonOptional<unknown[]>(section.vetting_notes) ?? (Array.isArray(section.vetting_notes) ? section.vetting_notes : null);
    return Array.isArray(notes) ? notes : null;
  })();
  return {
    vetting_status: optionalString(section.vetting_status) ?? (section.vetting_status as string | null | undefined) ?? null,
    active_status: optionalString(section.active_status) ?? null,
    data_quality_score: optionalNumber(section.data_quality_score),
    priority_score: optionalNumber(section.priority_score),
    carol_s_rating: optionalNumber(section.carol_s_rating),
    last_verified_date: optionalString(section.last_verified_date),
    vetting_notes: vettingNotes ?? (typeof section.vetting_notes === 'string' ? section.vetting_notes : null),
  };
};

const extractEligibility = (raw: unknown): ProgramCardEligibility | null => {
  const section = parseJsonOptional<Record<string, unknown>>(raw) ?? (isRecord(raw) ? raw : null);
  if (!section) return null;
  return {
    isbn_required: optionalBoolean(section.isbn_required),
    independent_publishers_allowed: optionalBoolean(section.independent_publishers_allowed),
    english_books_only: optionalBoolean(section.english_books_only),
    accepted_formats: parseJsonArray<string>(section.accepted_formats),
    publication_date_restrictions: optionalString(section.publication_date_restrictions),
    geographic_restrictions: parseJsonArray<string>(section.geographic_restrictions),
    requirements_summary: optionalString(section.requirements_summary),
    manuscript_requirements: optionalBoolean(section.manuscript_requirements),
    deadline_type: optionalString(section.deadline_type),
  };
};

const extractFeesPrizes = (raw: unknown): ProgramCardFeesPrizes | null => {
  const section = parseJsonOptional<Record<string, unknown>>(raw) ?? (isRecord(raw) ? raw : null);
  if (!section) return null;
  return {
    entry_fee_min: optionalNumber(section.entry_fee_min) ?? optionalString(section.entry_fee_min),
    entry_fee_max: optionalNumber(section.entry_fee_max) ?? optionalString(section.entry_fee_max),
    entry_fee_currency: optionalString(section.entry_fee_currency),
    multiple_prizes_offered: optionalBoolean(section.multiple_prizes_offered),
    total_win_opportunities: optionalNumber(section.total_win_opportunities),
    prize_types: parseJsonArray<string>(section.prize_types),
    extra_benefits: parseJsonArray<string>(section.extra_benefits),
    primary_currency: optionalString(section.primary_currency),
    has_tiered_prizes: optionalBoolean(section.has_tiered_prizes),
    prize_structure: optionalString(section.prize_structure),
  };
};

const extractTimeline = (raw: unknown): ProgramCardTimeline | null => {
  const section = parseJsonOptional<Record<string, unknown>>(raw) ?? (isRecord(raw) ? raw : null);
  if (!section) return null;
  return {
    early_bird_deadline: optionalString(section.early_bird_deadline),
    regular_deadline: optionalString(section.regular_deadline),
    final_deadline: optionalString(section.final_deadline),
    winners_announcement_date: optionalString(section.winners_announcement_date),
    award_ceremony_date: optionalString(section.award_ceremony_date),
    deadline_structure: optionalString(section.deadline_structure),
    submission_period: optionalString(section.submission_period),
    announcement_timeline: optionalString(section.announcement_timeline),
    any_date_present: optionalBoolean(section.any_date_present),
  };
};

const extractUrls = (raw: unknown): ProgramCardUrls | null => {
  const section = parseJsonOptional<Record<string, unknown>>(raw) ?? (isRecord(raw) ? raw : null);
  if (!section) return null;
  return {
    website_url: optionalString(section.website_url),
    submission_url: optionalString(section.submission_url),
    past_winners_url: optionalString(section.past_winners_url),
    wikipedia_entry: optionalString(section.wikipedia_entry),
    logo_url: optionalString(section.logo_url),
    website_screenshot_url: optionalString(section.website_screenshot_url),
  };
};

const extractContacts = (raw: unknown): ProgramCardContacts | null => {
  const section = parseJsonOptional<Record<string, unknown>>(raw) ?? (isRecord(raw) ? raw : null);
  if (!section) return null;
  return {
    contact_name: optionalString(section.contact_name),
    contact_email: optionalString(section.contact_email),
    contact_phone: optionalString(section.contact_phone),
    contact_notes: optionalString(section.contact_notes),
    physical_address: optionalString(section.physical_address),
  };
};

const extractProgramDetails = (raw: unknown): ProgramCardProgramDetails | null => {
  const section = parseJsonOptional<Record<string, unknown>>(raw) ?? (isRecord(raw) ? raw : null);
  if (!section) return null;
  return {
    host_country: optionalString(section.host_country),
    language_requirements: optionalString(section.language_requirements),
    submission_type: optionalString(section.submission_type),
    has_audiobook_categories: optionalBoolean(section.has_audiobook_categories),
    bisac_default_primary_code: optionalString(section.bisac_default_primary_code),
    bisac_default_secondary_code: optionalString(section.bisac_default_secondary_code),
    bisac_default_tertiary_code: optionalString(section.bisac_default_tertiary_code),
    bisac_preferred_edition_year: optionalString(section.bisac_preferred_edition_year) ?? optionalNumber(section.bisac_preferred_edition_year),
    industry_warnings: optionalString(section.industry_warnings),
  };
};

const extractCategories = (raw: unknown): ProgramCardCategory[] | null => {
  const arr = parseJsonArray<Record<string, unknown>>(raw);
  if (!arr.length) return null;
  return arr.map((entry) => (isRecord(entry) ? entry : {})).map((entry) => entry as ProgramCardCategory);
};

export const normalizeProgramCardPayload = (raw: unknown): ProgramCardPayload => {
  let source = raw;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source) as unknown;
    } catch {
      source = {};
    }
  }

  const base = isRecord(source) ? source : {};
  return {
    program: extractProgram(base.program),
    ratings_vetting: extractRatingsVetting(base.ratings_vetting),
    eligibility: extractEligibility(base.eligibility),
    fees_prizes: extractFeesPrizes(base.fees_prizes),
    timeline: extractTimeline(base.timeline),
    urls: extractUrls(base.urls),
    contacts: extractContacts(base.contacts),
    program_details: extractProgramDetails(base.program_details),
    judging: parseJsonOptional<Record<string, unknown>>(base.judging) ?? (isRecord(base.judging) ? base.judging : null),
    categories: extractCategories(base.categories),
    generated_at: optionalString(base.generated_at),
  };
};
